/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SsoCallbackPage
 * ----------------
 * The second half of the SSO OAuth round trip (D-01 through D-04): the page
 * the IdP redirects back to after the user authorizes the app.
 *
 * Responsibilities:
 *  - Reads `providerKey` from the route (`/auth/sso/callback/:providerKey`,
 *    D-02 — one distinct allowlisted redirect_uri per provider) and `state`/
 *    `code` from the query string.
 *  - Forwards `state`/`code` verbatim to the backend's `SsoCallbackAPIView`
 *    via a raw `fetch` call with `credentials: 'include'` (D-03 — this is
 *    mandatory, or the session cookie set by the backend's `login()` call
 *    will not persist client-side).
 *  - On success, reuses `runPostLoginFlow` from `postLogin.js` (D-04) — no
 *    inline re-derivation of the userData/store/dispatch logic here.
 *  - On a structured error, routes back to `/sign-in` with `sso_error` (and
 *    `provider` for the no-account-found case) so SignInPage can render the
 *    appropriate banner/prompt (D-05/D-06/D-07).
 *  - No client-side PKCE/nonce/state validation or token decoding — the
 *    backend performs all of that; this page only forwards values verbatim.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTheme } from "../theme/ThemeContext";
import envConfig from "../config/envConfig";
import { runPostLoginFlow } from "../utils/postLogin";
import { takeSsoNext } from "../utils/returnTo";

export default function SsoCallbackPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { providerKey } = useParams();
    const { theme } = useTheme();
    const [error, setError] = useState(null);

    // Guard against React StrictMode's double-invocation of effects — this
    // fetch is not idempotent from the user's perspective (the one-time-use
    // `state` value would 400 on a second call against the same code/state).
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        (async () => {
            try {
                const params = new URLSearchParams(location.search);
                const state = params.get("state") || "";
                const code = params.get("code") || "";

                const response = await fetch(
                    `${envConfig.backendApiUrl}/arcee/sso/${providerKey}/callback/?state=${encodeURIComponent(state)}&code=${encodeURIComponent(code)}`,
                    { credentials: "include" }
                );

                if (response.ok) {
                    const data = await response.json();
                    // Where the visitor was heading before SSO took over the
                    // browser. The password path reads this off /sign-in's own
                    // ?next=; this path cannot (the callback URL is allowlisted
                    // server-side and carries only state/code), so the sign-in
                    // and sign-up forms stash it before leaving. Absent or
                    // expired, "/dashboard" — the destination this page has
                    // always used — still applies.
                    const stashedNext = takeSsoNext();
                    await runPostLoginFlow(data, {
                        navigate,
                        defaultTarget: stashedNext || "/dashboard",
                    });
                    return;
                }

                const err = await response.json().catch(() => ({}));

                // A failed trip hands the return path BACK to the sign-in page
                // rather than dropping it: the visitor is about to try again
                // there (password, or "sign up instead?", which re-enters SSO),
                // and only a `next` that is in the URL survives into that
                // retry. Taken from the stash so the value lives in exactly one
                // place at a time.
                const retryNext = takeSsoNext();
                const withRetryNext = (url) =>
                    retryNext ? `${url}&next=${encodeURIComponent(retryNext)}` : url;

                // D-07: no-account-found is distinguished from the generic 400s so
                // SignInPage can offer "sign up instead?" with the provider pre-filled.
                if (err.error === "no_account_for_sso_provider") {
                    navigate(withRetryNext(`/sign-in?sso_error=no_account_for_sso_provider&provider=${encodeURIComponent(providerKey)}`), { replace: true });
                    return;
                }

                // D-05: every other error code falls through to the generic banner —
                // no `provider` param, and `err.error` is only ever used as a query
                // value, never reflected into HTML/attributes.
                navigate(withRetryNext(`/sign-in?sso_error=${encodeURIComponent(err.error || "sso_failed")}`), { replace: true });
            } catch (_networkErr) {
                setError("Something went wrong completing sign-in. Redirecting...");
                navigate("/sign-in?sso_error=sso_failed", { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
            <div className="text-center">
                <div className="spinner-border" role="status" style={{ color: theme.primary }} />
                <p className="mt-3 text-muted">{error || "Completing sign-in..."}</p>
            </div>
        </div>
    );
}
